import { api } from "@/lib/api";

/**
 * TV import lists (issue #72) — Sonarr-equivalent of the movie list
 * import that landed in #51. Today only Trakt show watchlists are
 * supported; the kind union is kept permissive so adding TMDB / IMDB
 * show lists later is a single-string change.
 */
export type TvImportListKind = "trakt-list";

/**
 * Sonarr-style monitor preset applied to every series this list
 * adds. Mirrors `MonitorPreset` on the series-add modal — the syncer
 * runs the same preset application on episodes after insert.
 */
export type TvMonitorPreset =
  | "all"
  | "future"
  | "missing"
  | "existing"
  | "first-season"
  | "last-season"
  | "none";

export interface TvImportList {
  id: number;
  name: string;
  kind: TvImportListKind;
  /** Trakt: `user/slug`. */
  source_url_or_id: string;
  monitor_preset: TvMonitorPreset;
  enabled: boolean;
  last_synced_at: string | null;
  hit_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateTvImportListRequest {
  name: string;
  kind: TvImportListKind;
  source_url_or_id: string;
  monitor_preset?: TvMonitorPreset;
  enabled?: boolean;
}

export interface UpdateTvImportListRequest {
  name?: string;
  kind?: TvImportListKind;
  source_url_or_id?: string;
  monitor_preset?: TvMonitorPreset;
  enabled?: boolean;
}

export interface TvSyncOutcome {
  tv_import_list_id: number;
  total_seen: number;
  added: number;
  already_present: number;
  unresolved: number;
  failed: number;
}

export const tvImportListsApi = {
  list: () => api<TvImportList[]>("/api/v1/tv/import-lists"),
  get: (id: number) => api<TvImportList>(`/api/v1/tv/import-lists/${id}`),
  create: (body: CreateTvImportListRequest) =>
    api<TvImportList>("/api/v1/tv/import-lists", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateTvImportListRequest) =>
    api<TvImportList>(`/api/v1/tv/import-lists/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/tv/import-lists/${id}`, { method: "DELETE" }),
  syncNow: (id: number) =>
    api<TvSyncOutcome>(`/api/v1/tv/import-lists/${id}/sync-now`, {
      method: "POST",
    }),
};
